import { CountryCode } from '@crehana/ts-types';

import { CountryPrefixEnum } from 'Jsx/CheckoutV4/types';
import { REGEX_CURP, NUMBERS_ONLY, REGEX_DNI_PERU } from 'Jsx/Shared/regex';

export const CARD_ERRORS = {
  INVALID_CARD_NUMBER: 1,
  INVALID_EXPIRATION_DATE: 2,
  INVALID_CVV: 3,
};

export const getCardErrorMessage = (errorCode: number) => {
  switch (errorCode) {
    case CARD_ERRORS.INVALID_CARD_NUMBER:
      return 'INVALID_CARD_NUMBER';

    case CARD_ERRORS.INVALID_EXPIRATION_DATE:
      return 'INVALID_EXPIRATION_DATE';

    case CARD_ERRORS.INVALID_CVV:
      return 'INVALID_CVC_CVV';

    default:
      return 'DEFAULT_PAYMENT_ERROR';
  }
};

export const getDNIText = (countryPrefix: CountryCode) => {
  switch (countryPrefix) {
    case CountryPrefixEnum.ARGENTINA:
      return 'DNI ó CUIT';
    case CountryPrefixEnum.MEXICO:
      return 'CURP';
    case CountryPrefixEnum.COLOMBIA:
      return 'CC';
    case CountryPrefixEnum.PERU:
      return 'DNI';
    case CountryPrefixEnum.CHILE:
      return 'CI/RUT';
    default:
      return 'DNI';
  }
};

export const getDNIDescription = (countryPrefix: CountryCode) => {
  switch (countryPrefix) {
    case CountryPrefixEnum.ARGENTINA:
      return 'Ejemplo: 5213535 O 52135353';
    case CountryPrefixEnum.MEXICO:
      return 'Ejemplo: ROVI490617HSPDSS05';
    case CountryPrefixEnum.COLOMBIA:
      return 'Ejemplo: 32480711';
    case CountryPrefixEnum.PERU:
      return 'Ejemplo: 300650890';
    case CountryPrefixEnum.CHILE:
      return 'Ejemplo: 163029001 ó 963029001';
    default:
      return '';
  }
};

export const getDniMaxLength = (countryPrefix: CountryCode) => {
  switch (countryPrefix) {
    case CountryPrefixEnum.ARGENTINA:
      return 11;
    case CountryPrefixEnum.MEXICO:
      return 18;
    case CountryPrefixEnum.COLOMBIA:
      return 10;
    case CountryPrefixEnum.PERU:
      return 9;
    case CountryPrefixEnum.CHILE:
      return 9;
    default:
      return 12;
  }
};

export const getEmptyDniErrorMessage = (countryPrefix: CountryCode) => {
  const dniType = getDNIText(countryPrefix);

  return `${dniType} obligatorio`;
};

export const getInvalidDniErrorMessage = (countryPrefix: CountryCode) => {
  const dniType = getDNIText(countryPrefix);

  return `${dniType} inválido`;
};

export const isValidDNI = (dni: string, countryPrefix: CountryCode) => {
  if (!dni || !countryPrefix) {
    return false;
  }

  switch (countryPrefix) {
    case CountryPrefixEnum.MEXICO: {
      const isValidDniLength = dni.length >= 10 && dni.length <= 18;

      return isValidDniLength && REGEX_CURP.test(dni);
    }
    case CountryPrefixEnum.ARGENTINA: {
      const isValidDniLength =
        (dni.length >= 7 && dni.length <= 9) || dni.length === 11;

      return isValidDniLength && NUMBERS_ONLY.test(dni);
    }
    case CountryPrefixEnum.PERU: {
      const isValidDniLength = dni.length === 8 || dni.length === 9;

      return isValidDniLength && REGEX_DNI_PERU.test(dni);
    }
    case CountryPrefixEnum.COLOMBIA: {
      const isValidDniLength = dni.length >= 6 && dni.length <= 10;

      return isValidDniLength && NUMBERS_ONLY.test(dni);
    }
    case CountryPrefixEnum.CHILE: {
      const isValidDniLength = dni.length === 8 || dni.length === 9;

      return isValidDniLength;
    }
    default:
      return false;
  }
};

const checksum = (s: string) => {
  let hash = 0;
  const strlen = s.length;

  if (strlen === 0) {
    return hash;
  }

  for (let i = 0; i < strlen; i += 1) {
    const c = s.charCodeAt(i);

    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + c;
    // eslint-disable-next-line no-bitwise
    hash &= hash; // Convert to 32bit integer
  }

  return Math.abs(hash);
};

export const generateDeviceId = () => {
  const nav = window.navigator;
  const { screen } = window;
  let deviceId: string = nav.mimeTypes.length.toString();

  Object.values(navigator.mimeTypes).forEach(p => {
    deviceId += p.type;
  });
  deviceId += nav.userAgent.replace(/\D+/g, '');
  deviceId += nav.plugins.length.toString();

  Object.values(navigator.plugins).forEach(p => {
    deviceId += p.filename;
  });

  deviceId += screen.height.toString() || '';
  deviceId += screen.width.toString() || '';
  deviceId += screen.pixelDepth.toString() || '';

  return checksum(deviceId);
};
